import { X } from 'lucide-react';
import { useEffect, type ButtonHTMLAttributes, type ReactNode } from 'react';
import s from './ui.module.css';

const cx = (...c: (string | false | undefined | null)[]): string => c.filter(Boolean).join(' ');

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'err' | 'cyber';

export function Button({
  variant = 'secondary',
  size = 'medium',
  loading,
  icon,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cx(s.button, variant !== 'secondary' && s[variant], size !== 'medium' && s[size], className)}
      disabled={loading || rest.disabled}
      {...rest}
    >
      {loading ? <span className={s.spinner} aria-hidden /> : icon}
      {children}
    </button>
  );
}

export function Panel({
  title,
  label,
  actions,
  children,
  flush,
  cut,
  className,
}: {
  title?: ReactNode;
  label?: string;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  cut?: boolean;
  className?: string;
}) {
  return (
    <section className={cx(s.panel, cut && s.panelCut, className)}>
      {(title || label || actions) && (
        <header className={s.panelHeader}>
          <div className={s.panelTitle}>
            {label && <span className="label">{label}</span>}
            {title && <h2>{title}</h2>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush ? s.panelBodyFlush : s.panelBody}>{children}</div>
    </section>
  );
}

export function Badge({ tone = 'neutral', children, icon }: { tone?: Tone; children: ReactNode; icon?: ReactNode }) {
  return (
    <span className={cx(s.badge, tone !== 'neutral' && s[`tone-${tone}`])}>
      {icon}
      {children}
    </span>
  );
}

export function Meter({
  label,
  value,
  max,
  display,
  color,
  tall,
}: {
  label?: ReactNode;
  value: number;
  max: number;
  display?: ReactNode;
  color?: string;
  tall?: boolean;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={cx(s.meter, tall && s.meterTall)}>
      {(label || display) && (
        <div className={s.meterTop}>
          <span>{label}</span>
          <span className="mono">{display ?? `${Math.round(value)}/${max}`}</span>
        </div>
      )}
      <div className={s.meterTrack} role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
        <div className={s.meterFill} style={{ width: `${pct}%`, ...(color ? { background: color } : {}) }} />
      </div>
    </div>
  );
}

export function Stat({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <div className={s.stat}>
      <span className="label">{label}</span>
      <span className={s.statValue}>
        {value}
        {unit && <span className={s.statUnit}>{unit}</span>}
      </span>
    </div>
  );
}

export function Spinner() {
  return <span className={s.spinner} aria-label="Loading" />;
}

export function EmptyState({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className={s.empty}>
      {icon}
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={cx(s.modal, wide && s.modalWide)} role="dialog" aria-modal="true">
        <header className={s.modalHeader}>
          <h2>{title}</h2>
          <Button variant="ghost" size="small" onClick={onClose} aria-label="Close" icon={<X size={16} />} />
        </header>
        <div className={s.modalBody}>{children}</div>
        {footer && <footer className={s.modalFooter}>{footer}</footer>}
      </div>
    </div>
  );
}

export function Field({ label, error, children }: { label: string; error?: string | undefined; children: ReactNode }) {
  return (
    <label className={s.field}>
      <span className="label">{label}</span>
      {children}
      {error && <span className={s.fieldError}>{error}</span>}
    </label>
  );
}

export const inputClass = s.input;
export const selectClass = s.select;

export function Alert({ children, info }: { children: ReactNode; info?: boolean }) {
  return <div className={cx(s.alert, info && s.alertInfo)}>{children}</div>;
}

export { cx };
